import type {
	DayModel,
	HourModel,
	OpenWeekModel,
	StationModel,
} from '@/types/api-models'
import { isWithinInterval, parseISO, startOfDay } from 'date-fns'

function timeToMinutes(time: string): number {
	const [h = '0', m = '0'] = time.split(':')
	return Number(h) * 60 + Number(m)
}

export function normalizeHourToHHMM(
	raw: string | undefined | null,
): string | null {
	if (raw == null || String(raw).trim() === '') return null
	const s = String(raw).trim()
	const m = s.match(/(\d{1,2})\s*:\s*(\d{2})/)
	if (!m) return null
	const h = Math.min(23, Math.max(0, parseInt(m[1], 10)))
	const min = Math.min(59, Math.max(0, parseInt(m[2], 10)))
	return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`
}

const WEEKDAY_MAP: Record<string, number> = {
	SUN: 0,
	SUNDAY: 0,
	DOM: 0,
	MON: 1,
	MONDAY: 1,
	LUN: 1,
	TUE: 2,
	TUESDAY: 2,
	MAR: 2,
	WED: 3,
	WEDNESDAY: 3,
	MIE: 3,
	THU: 4,
	THURSDAY: 4,
	JUE: 4,
	FRI: 5,
	FRIDAY: 5,
	VIE: 5,
	SAT: 6,
	SATURDAY: 6,
	SAB: 6,
}

/**
 * ¿El calendario `date` coincide con `codeDay` del API?
 */
export function dateMatchesStationCodeDay(
	date: Date,
	codeDay: string | number,
): boolean {
	const raw = codeDay
	if (typeof raw === 'string') {
		const t = raw.trim()
		const upper = t.toUpperCase()
		const n = parseInt(t, 10)
		if (!Number.isNaN(n) && t === String(n)) {
			return dateMatchesStationCodeDay(date, n)
		}
		const key3 = upper.slice(0, 3)
		const dow = WEEKDAY_MAP[upper] ?? WEEKDAY_MAP[key3]
		if (dow !== undefined) return date.getDay() === dow
		return false
	}

	const num = Number(raw)
	if (Number.isNaN(num)) return false
	const js = date.getDay()

	if (num >= 1 && num <= 7) {
		const isoDow = js === 0 ? 7 : js
		return isoDow === num
	}
	if (num >= 0 && num <= 6) return js === num
	return false
}

function coerceHour(raw: unknown): HourModel | null {
	if (!raw || typeof raw !== 'object') return null
	const h = raw as Record<string, unknown>
	const start = String(h.startHour ?? h.StartHour ?? '')
	const end = String(h.endHour ?? h.EndHour ?? '')
	if (!start.trim() || !end.trim()) return null
	const bt = h.businessType ?? h.BusinessType
	return {
		startHour: start,
		endHour: end,
		...(bt != null ? { businessType: String(bt) } : {}),
	}
}

function coerceDay(raw: unknown): DayModel | null {
	if (!raw || typeof raw !== 'object') return null
	const d = raw as Record<string, unknown>
	const hoursRaw = d.hours ?? d.Hours
	if (!Array.isArray(hoursRaw)) return null
	const hours = hoursRaw
		.map(coerceHour)
		.filter((x): x is HourModel => Boolean(x))
	const codeDay = d.codeDay ?? d.CodeDay
	if (codeDay === undefined || codeDay === null) return null
	return { codeDay: codeDay as string | number, hours }
}

function coerceOpenWeek(raw: unknown): OpenWeekModel | null {
	if (!raw || typeof raw !== 'object') return null
	const w = raw as Record<string, unknown>
	const daysRaw = w.days ?? w.Days
	if (!Array.isArray(daysRaw)) return null
	const days = daysRaw
		.map(coerceDay)
		.filter((x): x is DayModel => Boolean(x))
	if (days.length === 0) return null
	const startDate = w.startDate ?? w.StartDate
	const endDate = w.endDate ?? w.EndDate
	return {
		days,
		startDate:
			typeof startDate === 'string' ? startDate : undefined,
		endDate: typeof endDate === 'string' ? endDate : undefined,
	}
}

function readOpenWeeksFromRaw(r: Record<string, unknown>): OpenWeekModel[] {
	const raw = r.openWeeks ?? r.OpenWeeks
	if (!Array.isArray(raw)) return []
	return raw
		.map(coerceOpenWeek)
		.filter((x): x is OpenWeekModel => Boolean(x))
}

/**
 * Normaliza una fila de `/stations` (camelCase / PascalCase mezclados).
 */
export function normalizeStationFromApi(raw: unknown): StationModel {
	if (!raw || typeof raw !== 'object') {
		return { id: '', name: '' }
	}
	const r = raw as Record<string, unknown>
	const id = String(r.id ?? r.stationID ?? r.StationID ?? '').trim()
	const name = String(
		r.name ?? r.station ?? r.Station ?? r.stationName ?? r.StationName ?? '',
	).trim()
	const openWeeks = readOpenWeeksFromRaw(r)
	const rawOpen = (
		r.weekDayOpen ??
		r.WeekDayOpen ??
		''
	).toString().trim()
	const rawClose = (
		r.weekDayClose ??
		r.WeekDayClose ??
		''
	).toString().trim()
	return {
		id,
		name,
		openWeeks: openWeeks.length > 0 ? openWeeks : undefined,
		...(rawOpen && rawClose ? { weekDayOpen: rawOpen, weekDayClose: rawClose } : {}),
	}
}

function weekAppliesToDate(week: OpenWeekModel, date: Date): boolean {
	if (!week.startDate?.trim() && !week.endDate?.trim()) return true

	const day = startOfDay(date)
	try {
		const start = week.startDate
			? startOfDay(parseISO(week.startDate.slice(0, 10)))
			: undefined
		const end = week.endDate
			? startOfDay(parseISO(week.endDate.slice(0, 10)))
			: undefined

		if (start && end) {
			const lo = start <= end ? start : end
			const hi = start <= end ? end : start
			return isWithinInterval(day, { start: lo, end: hi })
		}
		if (start) return day >= start
		if (end) return day <= end
	} catch {
		return true
	}
	return true
}

function getActiveOpenWeeks(station: StationModel, date: Date): OpenWeekModel[] {
	const weeks = station.openWeeks ?? []
	if (!weeks.length) return []

	const withSeason = weeks.filter(w => w.startDate || w.endDate)
	const matchingSeason = withSeason.filter(w => weekAppliesToDate(w, date))
	if (matchingSeason.length > 0) return matchingSeason

	const general = weeks.filter(w => !w.startDate && !w.endDate)
	if (general.length > 0) return general
	return weeks
}

export interface TimeInterval {
	start: string
	end: string
	businessType?: string
}

function getWeekDayOpenCloseInterval(
	station: StationModel | undefined,
): TimeInterval | null {
	if (!station) return null
	const rawOpen = (station.weekDayOpen ?? '').toString().trim()
	const rawClose = (station.weekDayClose ?? '').toString().trim()
	if (!rawOpen || !rawClose || rawOpen === rawClose) return null
	const open = normalizeHourToHHMM(
		rawOpen.includes(':') ? rawOpen : `${rawOpen}:00`,
	)
	const close = normalizeHourToHHMM(
		rawClose.includes(':') ? rawClose : `${rawClose}:00`,
	)
	if (!open || !close || open === close) return null
	return { start: open, end: close }
}

/** Tiene openWeeks o par weekDayOpen/weekDayClose usable. */
export function stationHasOfficeHourRules(
	station: StationModel | undefined,
): boolean {
	if (!station) return false
	if (station.openWeeks?.length) return true
	return getWeekDayOpenCloseInterval(station) !== null
}

interface StationDayResolution {
	matchedCodeDay: boolean
	intervals: TimeInterval[]
}

/**
 * Resuelve los intervalos para una fecha + indica si openWeeks declaró explícitamente ese codeDay.
 * Si el codeDay aparece en openWeeks pero todas sus horas están en blanco → matchedCodeDay=true, intervals=[]
 * (la estación está cerrada ese día, no se debe caer a weekDayOpen/Close).
 */
function resolveStationDayHours(
	station: StationModel | undefined,
	date: Date | undefined,
): StationDayResolution {
	if (!station || !date || Number.isNaN(date.getTime())) {
		return { matchedCodeDay: false, intervals: [] }
	}

	const intervals: TimeInterval[] = []
	let matchedCodeDay = false

	if (station.openWeeks?.length) {
		for (const week of getActiveOpenWeeks(station, date)) {
			for (const day of week.days) {
				if (!dateMatchesStationCodeDay(date, day.codeDay)) continue
				matchedCodeDay = true
				for (const h of day.hours) {
					const s = normalizeHourToHHMM(h.startHour)
					const e = normalizeHourToHHMM(h.endHour)
					if (s && e) intervals.push({ start: s, end: e, ...(h.businessType ? { businessType: h.businessType } : {}) })
				}
			}
		}
	}

	if (!matchedCodeDay && intervals.length === 0) {
		const fallback = getWeekDayOpenCloseInterval(station)
		if (fallback) intervals.push(fallback)
	}

	return { matchedCodeDay, intervals }
}

/**
 * Franjas HH:mm abiertas para esa estación en la fecha calendario.
 * Prioridad: intervalos de openWeeks para ese día; si no hay ninguno, weekDayOpen/Close.
 */
export function getStationOpenIntervalsForDate(
	station: StationModel | undefined,
	date: Date | undefined,
): TimeInterval[] {
	return resolveStationDayHours(station, date).intervals
}

function isSlotInsideAnyInterval(
	slotHHmm: string,
	intervals: TimeInterval[],
): boolean {
	const t = timeToMinutes(slotHHmm)
	for (const { start, end } of intervals) {
		const a = timeToMinutes(start)
		const b = timeToMinutes(end)
		if (b >= a) {
			if (t >= a && t <= b) return true
		} else {
			if (t >= a || t <= b) return true
		}
	}
	return false
}

/**
 * `true` si el slot de 30 min está fuera de toda franja abierta.
 * Sin openWeeks ni weekDayOpen/Close válidos → no se restringe.
 */
export function isSlotOutsideStationOpenHours(
	station: StationModel | undefined,
	calendarDate: Date | undefined,
	slotHHmm: string,
): boolean {
	if (!station || !calendarDate) return false

	const intervals = getStationOpenIntervalsForDate(station, calendarDate)
	if (intervals.length === 0) return false

	return !isSlotInsideAnyInterval(slotHHmm, intervals)
}

/**
 * Estado del slot según la configuración real de la estación (businessType de la API).
 * - 'normal'      → dentro de un intervalo NORMAL (sin recargo)
 * - 'after-hours' → dentro de un intervalo AFTER (con recargo)
 * - 'closed'      → la estación tiene datos para esa fecha pero el slot está fuera de todos los intervalos
 * - null          → la estación no tiene datos de horario para esa fecha → usar lógica global de fallback
 */
export type SlotStationStatus = 'normal' | 'after-hours' | 'closed' | null

export function getSlotStationStatus(
	station: StationModel | undefined,
	calendarDate: Date | undefined,
	slotHHmm: string,
): SlotStationStatus {
	if (!station || !calendarDate) return null
	const { matchedCodeDay, intervals } = resolveStationDayHours(station, calendarDate)
	if (!intervals.length) {
		return matchedCodeDay ? 'closed' : null
	}
	const t = timeToMinutes(slotHHmm)
	for (const { start, end, businessType } of intervals) {
		const a = timeToMinutes(start)
		const b = timeToMinutes(end)
		const inside = b >= a ? t >= a && t <= b : t >= a || t <= b
		if (inside) return businessType?.toUpperCase() === 'AFTER' ? 'after-hours' : 'normal'
	}
	return 'closed'
}

export type SlotGlobalStatus = 'after-hours' | 'closed' | 'normal'

/**
 * 22:00–6:00 → 'closed' (no seleccionable)
 * 6:00–8:00 y 21:00–22:00 → 'after-hours' (seleccionable con recargo)
 * 8:00–21:00 → 'normal' (sujeto a horario de estación)
 */
export function getSlotGlobalStatus(slotHHmm: string): SlotGlobalStatus {
	const t = timeToMinutes(slotHHmm)
	const t6 = 6 * 60    // 360
	const t8 = 8 * 60    // 480
	const t21 = 21 * 60  // 1260
	const t22 = 22 * 60  // 1320
	if (t >= t22 || t < t6) return 'closed'
	if (t < t8 || t >= t21) return 'after-hours'
	return 'normal'
}

/** Texto para ayuda en modal: primeras franjas del día elegido */
export function formatStationIntervalsLabel(
	station: StationModel | undefined,
	calendarDate: Date | undefined,
): string | null {
	const intervals = getStationOpenIntervalsForDate(station, calendarDate)
	if (!intervals.length) return null
	return intervals.map(({ start, end }) => `${start}–${end}`).join(', ')
}
